import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Markdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { modalVariants } from 'utils/frame-motion-variants';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import BottomSheet from '../commons/BottomSheet';

export const SystemError = (props) => {
    const header = props.errorContent.header;
    const body = props.errorContent.body;
    const isInfo = props.errorContent.isInfo || false;
    const setIsSystemError = props.setIsSystemError;

    const [mounted, setMounted] = useState(false);
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        setMounted(true);
    }, []);

    // Called after BottomSheet animation completes (for info mode)
    const handleClose = () => {
        setIsSystemError(false);
    };

    if (!mounted) return null;

    // Info mode - Use BottomSheet component
    if (isInfo) {
        return (
            <BottomSheet isOpen={isOpen} onClose={handleClose} maxWidth="md">
                <BottomSheet.Header>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <BottomSheet.Title>{header}</BottomSheet.Title>
                    </div>
                </BottomSheet.Header>

                <BottomSheet.Content>
                    {body && (
                        <div className={`text-gray-600 whitespace-pre-wrap leading-relaxed ${body.length > 200 ? 'text-sm' : 'text-base'}`}>
                            <Markdown>{body}</Markdown>
                        </div>
                    )}
                </BottomSheet.Content>

                <BottomSheet.Footer>
                    <BottomSheet.CloseButton variant="primary" className="w-full">Got It</BottomSheet.CloseButton>
                </BottomSheet.Footer>
            </BottomSheet>
        );
    }

    // Error mode - Original centered modal style
    return createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex justify-center items-center p-2 backdrop-blur-sm bg-black/30">
                <motion.div 
                    className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
                    variants={modalVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    {/* Header - Red theme for error */}
                    <div className="bg-linear-to-r from-red-500 to-rose-500 flex items-center justify-between px-5 py-4">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-white" />
                            <h3 className="text-lg font-bold text-white">{header}</h3>
                        </div>
                        <button 
                            type="button" 
                            onClick={() => setIsSystemError(false)} 
                            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="bg-red-50 p-5 space-y-4">
                        {/* Error Body */}
                        {body && (
                            <div className={`text-gray-600 whitespace-pre-wrap leading-relaxed ${body.length > 200 ? 'text-sm' : 'text-base'}`}>
                                <Markdown>{body}</Markdown>
                            </div>
                        )}

                        {/* Close Button */}
                        <div className="pt-2">
                            <motion.button 
                                type="button" 
                                onClick={() => setIsSystemError(false)}
                                className="w-full px-6 py-3 bg-linear-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 rounded-xl text-white font-bold shadow-lg flex items-center justify-center gap-2"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Got It
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
}
